import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const IMGS = {
  campus: 'https://images.unsplash.com/photo-1562774053-701939374585?w=1400&q=85&fit=crop',
  students: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1400&q=80&fit=crop',
  hero: 'https://images.unsplash.com/photo-1607237138185-eedd9c632b0b?w=1800&q=85&fit=crop',
};

const About: React.FC = () => (
  <>
    <Navbar />

    <section
      className="hero"
      style={{
        padding: '90px 24px',
        backgroundImage: `linear-gradient(160deg, rgba(4,15,30,0.90) 0%, rgba(11,37,69,0.82) 100%), url(${IMGS.hero})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className="hero-content">
        <span className="hero-eyebrow">About the University</span>
        <h1>Built for the Global Learner</h1>
        <p>An international online university designed to deliver accessible, high-quality education through the convergence of academic rigor and digital innovation.</p>
      </div>
    </section>

    {/* Mission / Vision */}
    <section className="section">
      <div className="container">
        <div className="about-grid">
          <div>
            <span className="section-eyebrow">Our Institution</span>
            <h2 className="section-title">Where Scholarship Meets Innovation</h2>
            <div className="divider"></div>
            <p style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", color: 'var(--text-light)', lineHeight: 1.85, marginBottom: 20, fontSize: '0.93rem' }}>
              The University of American Atlantic is an international online university established to deliver accessible, high-quality education to learners around the world. Located in Pinellas County, Florida, UAA combines academic knowledge, professional experience, and digital innovation to create an educational system aligned with contemporary global standards.
            </p>
            <p style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", color: 'var(--text-light)', lineHeight: 1.85, marginBottom: 32, fontSize: '0.93rem' }}>
              UAA welcomes students from more than 60 countries and offers a dynamic learning environment supported by AI-assisted instruction, modular credential pathways, and a globally recognized diploma verification system.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              <div style={{ borderTop: '2px solid var(--gold)', paddingTop: 16 }}>
                <div style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", fontSize: '0.68rem', fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: 8 }}>Mission</div>
                <p style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", fontSize: '0.87rem', color: 'var(--text-light)', lineHeight: 1.7 }}>
                  To deliver innovative and high-quality education that prepares students for contemporary professional and academic careers.
                </p>
              </div>
              <div style={{ borderTop: '2px solid var(--navy)', paddingTop: 16 }}>
                <div style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", fontSize: '0.68rem', fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: 'var(--navy)', marginBottom: 8 }}>Vision</div>
                <p style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", fontSize: '0.87rem', color: 'var(--text-light)', lineHeight: 1.7 }}>
                  To become a globally respected university recognized for excellence in digital education and international academic leadership.
                </p>
              </div>
            </div>
          </div>
          {/* Real campus image */}
          <div style={{ borderRadius: 3, overflow: 'hidden', border: '3px solid var(--gold)' }}>
            <img
              src={IMGS.campus}
              alt="University campus"
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', minHeight: 340 }}
            />
          </div>
        </div>
      </div>
    </section>

    {/* Values */}
    <section className="section section-navy">
      <div className="container">
        <span className="section-eyebrow">Core Values</span>
        <h2 className="section-title">The Principles That Guide Us</h2>
        <div className="divider"></div>
        <div className="values-grid">
          {['Innovation', 'Accessibility', 'Inclusiveness', 'Scholarly Distinction', 'Lifelong Education', 'Flexibility', 'Student-Centered Learning', 'Global Perspective'].map(v => (
            <div className="value-card" key={v}>
              <div className="v-line"></div>
              <h4>{v}</h4>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Students / Campus Life Banner */}
    <div
      style={{
        height: 380,
        backgroundImage: `linear-gradient(90deg, rgba(11,37,69,0.94) 0%, rgba(11,37,69,0.65) 50%, rgba(11,37,69,0.2) 100%), url(${IMGS.students})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        display: 'flex',
        alignItems: 'center',
        padding: '0 24px',
      }}
    >
      <div style={{ maxWidth: 1200, margin: '0 auto', width: '100%' }}>
        <span style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", fontSize: '0.7rem', fontWeight: 700, letterSpacing: '2.5px', textTransform: 'uppercase', color: 'rgba(184,150,46,0.85)', display: 'block', marginBottom: 14 }}>Student Community</span>
        <h2 style={{ fontFamily: "'Georgia', serif", color: '#fff', fontSize: 'clamp(1.5rem, 3vw, 2rem)', maxWidth: 520, lineHeight: 1.35, marginBottom: 12 }}>
          A collaborative academic environment connecting students from across the globe.
        </h2>
        <p style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem', maxWidth: 420, lineHeight: 1.7, marginBottom: 24 }}>
          More than 60 countries represented. One shared goal: academic excellence.
        </p>
        <Link to="/apply" className="btn-primary">Join UAA Today</Link>
      </div>
    </div>

    {/* RPL */}
    <section className="section section-light">
      <div className="container">
        <div style={{ maxWidth: 700, marginBottom: 52 }}>
          <span className="section-eyebrow">RPL System</span>
          <h2 className="section-title">From Professional Experience to Academic Recognition</h2>
          <div className="divider"></div>
          <p style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", color: 'var(--text-light)', lineHeight: 1.8, fontSize: '0.93rem' }}>
            The UAA Recognition of Prior Learning (RPL) system formally acknowledges that education occurs not only within classroom walls, but through professional practice, research, leadership, and real-world expertise.
          </p>
        </div>
        <div className="feature-grid">
          {[
            { num: '01', title: 'Professional Experience', desc: 'Verified years of industry experience are assessed and converted into academic credits toward your degree.' },
            { num: '02', title: 'Certifications & Training', desc: 'Industry-recognized certifications and professional training programs are formally evaluated for credit equivalency.' },
            { num: '03', title: 'Accelerated Completion', desc: 'Students entering through the RPL pathway may begin at an advanced level, significantly reducing time to graduation.' },
            { num: '04', title: 'Reduced Financial Burden', desc: 'By converting existing expertise into credits, RPL students reduce total tuition requirements and study duration.' },
          ].map(f => (
            <div className="feature-card" key={f.num}>
              <div className="fc-num">{f.num}</div>
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Diploma Verification */}
    <section className="section section-dark">
      <div className="container" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 72, alignItems: 'center' }}>
        <div>
          <span className="section-eyebrow">USDIS Platform</span>
          <h2 className="section-title">Global Diploma Verification</h2>
          <div className="divider"></div>
          <p style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", color: 'rgba(255,255,255,0.55)', lineHeight: 1.8, fontSize: '0.93rem', marginBottom: 18 }}>
            All diplomas issued by UAA are secured through the United States Diploma Inquiry Service (USDIS), a global verification platform that provides digital validation of academic credentials.
          </p>
          <p style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", color: 'rgba(255,255,255,0.55)', lineHeight: 1.8, fontSize: '0.93rem' }}>
            QR codes embedded on each diploma allow employers, institutions, and governments to instantly verify authenticity, transcripts, and academic records from anywhere in the world.
          </p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {['Diploma authenticity verification', 'Official academic transcript validation', 'Global digital accessibility', 'QR-code secured credentials', 'Compatible with international hiring standards'].map(item => (
            <div key={item} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', borderLeft: '3px solid var(--gold)', padding: '14px 18px', fontFamily: "'Segoe UI', system-ui, sans-serif", fontSize: '0.88rem', color: 'rgba(255,255,255,0.65)' }}>
              {item}
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="section section-navy" style={{ padding: '72px 24px', textAlign: 'center' }}>
      <div className="container">
        <span className="section-eyebrow" style={{ display: 'block', textAlign: 'center' }}>Get in Touch</span>
        <h2 className="section-title" style={{ textAlign: 'center', margin: '12px 0' }}>Have Questions About UAA?</h2>
        <div className="divider" style={{ margin: '0 auto 28px' }}></div>
        <p style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", color: 'rgba(255,255,255,0.5)', fontSize: '0.93rem', maxWidth: 480, margin: '0 auto 32px', lineHeight: 1.75 }}>Our admissions team is available to answer any questions about programs, requirements, or the RPL process.</p>
        <Link to="/contact" className="btn-primary">Contact the Admissions Office</Link>
      </div>
    </section>

    <Footer />
  </>
);

export default About;
